/**
 * WEGO Favorites App - ブランドお気に入りボタン機能
 * ブランドページのお気に入りボタン制御
 *
 * Version: 2026-02-04-vfa (v3)
 *
 * 仕様:
 * - 未登録時: 「♡お気に入りブランドに登録」(黒文字・白背景)
 * - 登録済み時: 「♡お気に入りブランド解除」(白文字・黒背景・枠あり)
 * - トグル動作: クリックで登録/解除が切り替わる
 * - 連打対策: 1秒間ボタン無効化 + 楽観的UI
 * - API: App Proxy経由でアクセス
 *
 * 必要なHTML要素:
 * - ボタン: .vfa-p-brand-header__favorite
 * - テキスト: .vfa-brand-favorite-text
 * - 属性: data-brand-tag（必須）, data-brand-id（オプション）
 */

(function () {
  "use strict";

  console.log("[VFA Brand Favorite] Script loaded - Version 2026-02-04-vfa");

  // APIエンドポイント（App Proxy経由）
  const BRAND_API_BASE = "/apps/wego-favorite/api/brands/favorite";
  const BRAND_LIST_API_BASE = "/apps/wego-favorite/api/brands/favorites";

  // デバッグモード（本番環境では false に設定）
  const DEBUG = false;

  // 設定
  const BUTTON_DISABLE_TIME = 1000; // 1秒間ボタン無効化
  const MAX_RETRIES = 3;
  const TEXT_REGISTER = "お気に入りブランドに登録";
  const TEXT_UNREGISTER = "お気に入りブランド解除";

  // ボタンの色設定（本番デザイン準拠）
  const COLOR_FAVORITED_BG = "#000"; // 登録済み背景色（黒）
  const COLOR_FAVORITED_TEXT = "#fff"; // 登録済み文字色（白）
  const COLOR_DEFAULT = ""; // デフォルト色（CSSに任せる）

  /**
   * ログ出力
   */
  function log(...args) {
    if (DEBUG) {
      console.log("[VFA Brand Favorite]", ...args);
    }
  }

  /**
   * 顧客IDを取得
   * @returns {string|null} 顧客ID（Shopify GID形式に変換）
   */
  function getCustomerId() {
    // Shopify Customerオブジェクトから取得
    if (window.ShopifyCustomer && window.ShopifyCustomer.id) {
      const id = String(window.ShopifyCustomer.id);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    // __stオブジェクトから取得
    if (window.__st && window.__st.cid) {
      const id = String(window.__st.cid);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    // メタタグから取得
    const metaCustomerId = document.querySelector(
      'meta[name="shopify-customer-id"]',
    );
    if (metaCustomerId && metaCustomerId.content) {
      const id = metaCustomerId.content;
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    return null;
  }

  /**
   * ログイン状態をチェック
   * @returns {boolean} ログイン済みかどうか
   */
  function isLoggedIn() {
    const customerId = getCustomerId();
    if (!customerId) {
      log("❌ 顧客IDが取得できません");
      return false;
    }
    log("✅ 顧客ID取得:", customerId);
    return true;
  }

  /**
   * ブランドお気に入り状態を確認
   * @param {string} brandTag - ブランドタグ
   * @param {HTMLElement} button - ボタン要素
   */
  async function checkBrandFavoriteStatus(brandTag, button) {
    if (!isLoggedIn()) {
      log("⏭️ 未ログインのため状態確認をスキップ");
      return;
    }

    try {
      // App Proxy経由の場合、customerIdをクエリパラメータとして追加しない
      // Shopify App Proxyが自動的にlogged_in_customer_idを追加するため
      const response = await fetch(BRAND_LIST_API_BASE);
      if (response.ok) {
        const data = await response.json();
        const isFavorited = data.favorites?.some(
          (f) => f.brandTag === brandTag,
        );
        updateButtonState(button, isFavorited);
        log(
          "📊 ブランド初期状態:",
          brandTag,
          isFavorited ? "お気に入り済み" : "未登録",
        );
      } else {
        log("⚠️ ブランド状態確認エラー: HTTP", response.status);
      }
    } catch (error) {
      log("⚠️ ブランド状態確認エラー:", error);
    }
  }

  /**
   * ブランドお気に入り追加/削除
   * @param {HTMLElement} button - ボタン要素
   */
  async function toggleBrandFavorite(button) {
    const brandTag = button.dataset.brandTag;

    log("🖱️ ブランドクリック:", brandTag);

    // ログインチェック
    if (!isLoggedIn()) {
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname);
      return;
    }

    // 楽観的UI更新: 即座に状態を変更
    const isCurrentlyFavorited = button.classList.contains("vfa-registered");
    const newFavoritedState = !isCurrentlyFavorited;
    updateButtonState(button, newFavoritedState);

    // ボタンを無効化
    button.disabled = true;
    button.classList.add("vfa-is-loading");

    const action = isCurrentlyFavorited ? "remove" : "add";

    // リトライ機能付きでAPIリクエストを送信
    let retryCount = 0;
    let lastError = null;

    while (retryCount <= MAX_RETRIES) {
      try {
        // customerIdをリクエストボディに含める（フォールバック用）
        // Shopify App Proxyが自動的にlogged_in_customer_idを追加するため、
        // サーバー側でlogged_in_customer_idを優先的に使用する
        const customerId = getCustomerId();

        const response = await fetch(BRAND_API_BASE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            brandTag: brandTag,
            action: action,
            customerId: customerId, // フォールバック用
          }),
        });

        if (response.ok) {
          log(
            `✅ ブランド${action === "add" ? "追加" : "削除"}成功:`,
            brandTag,
          );
          break; // 成功したらループを抜ける
        } else {
          const errorData = await response.json().catch(() => ({}));

          // 認証エラーの場合はリトライしない
          if (response.status === 401 || response.status === 403) {
            alert("認証エラーが発生しました。ログインページへ移動します。");
            window.location.href =
              "/account/login?return_url=" +
              encodeURIComponent(window.location.pathname);
            updateButtonState(button, isCurrentlyFavorited);
            return;
          }

          // サーバーエラー（5xx）の場合はリトライ
          if (response.status >= 500 && retryCount < MAX_RETRIES) {
            retryCount++;
            log(
              `⚠️ サーバーエラー (${response.status})。リトライ ${retryCount}/${MAX_RETRIES}...`,
            );
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * retryCount),
            );
            continue;
          }

          throw new Error(
            `API Error: ${response.status} - ${errorData.error || ""}`,
          );
        }
      } catch (error) {
        lastError = error;

        // ネットワークエラーの場合はリトライ
        if (
          retryCount < MAX_RETRIES &&
          (error.name === "TypeError" || error.message.includes("fetch"))
        ) {
          retryCount++;
          log(
            `⚠️ ネットワークエラー。リトライ ${retryCount}/${MAX_RETRIES}...`,
          );
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * retryCount),
          );
          continue;
        }

        // リトライ上限に達した場合
        log("❌ ブランドエラー:", error);
        showErrorMessage(
          "お気に入りの更新に失敗しました。もう一度お試しください。",
        );
        // 失敗時は元に戻す
        updateButtonState(button, isCurrentlyFavorited);
        break;
      }
    }

    // 最終的にエラーが残っている場合
    if (lastError && retryCount >= MAX_RETRIES) {
      log("❌ ブランドエラー（リトライ上限）:", lastError);
      showErrorMessage(
        "エラーが発生しました。しばらく時間をおいてから再度お試しください。",
      );
      updateButtonState(button, isCurrentlyFavorited);
    }

    // 1秒後にボタンを有効化
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove("vfa-is-loading");
    }, BUTTON_DISABLE_TIME);
  }

  /**
   * ボタンの状態を更新
   * @param {HTMLElement} button - ボタン要素
   * @param {boolean} isFavorited - お気に入り済みかどうか
   */
  function updateButtonState(button, isFavorited) {
    const textElement = button.querySelector(".vfa-brand-favorite-text");

    if (isFavorited) {
      button.classList.add("vfa-registered");
      button.setAttribute("aria-pressed", "true");
      button.setAttribute("aria-label", TEXT_UNREGISTER);
      button.setAttribute("title", TEXT_UNREGISTER);
      if (textElement) textElement.textContent = TEXT_UNREGISTER;

      // ボタンの色を直接変更（CSSが設定されていない場合のフォールバック）
      button.style.backgroundColor = COLOR_FAVORITED_BG;
      button.style.borderColor = COLOR_FAVORITED_BG;
      button.style.color = COLOR_FAVORITED_TEXT;
    } else {
      button.classList.remove("vfa-registered");
      button.setAttribute("aria-pressed", "false");
      button.setAttribute("aria-label", TEXT_REGISTER);
      button.setAttribute("title", TEXT_REGISTER);
      if (textElement) textElement.textContent = TEXT_REGISTER;

      // ボタンの色を元に戻す
      button.style.backgroundColor = COLOR_DEFAULT;
      button.style.borderColor = COLOR_DEFAULT;
      button.style.color = COLOR_DEFAULT;
    }

    log("🔄 ブランド状態更新:", button.dataset.brandTag, isFavorited);
  }

  /**
   * エラーメッセージを表示
   */
  function showErrorMessage(message) {
    // 既存のエラー表示があれば削除
    const existingError = document.querySelector(".vfa-brand-favorite-error");
    if (existingError) {
      existingError.remove();
    }

    // エラーメッセージ要素を作成
    const errorDiv = document.createElement("div");
    errorDiv.className = "vfa-brand-favorite-error";
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: #ff4444;
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;

    document.body.appendChild(errorDiv);

    // 3秒後に自動で消える
    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }

  /**
   * ブランドお気に入りボタンを初期化
   */
  function initializeBrandFavoriteButtons() {
    const buttons = document.querySelectorAll(".vfa-p-brand-header__favorite");

    if (buttons.length === 0) {
      return;
    }

    log(`📍 ブランドボタン ${buttons.length}個を検出`);

    buttons.forEach((button, index) => {
      if (button.dataset.vfaInitialized === "true") {
        return;
      }

      const brandTag = button.dataset.brandTag;

      if (!brandTag) {
        log(`⚠️ ブランドボタン${index + 1}: ブランドタグが見つかりません`);
        return;
      }

      if (!brandTag.startsWith("brand:")) {
        log(
          `⚠️ ブランドボタン${index + 1}: ブランドタグの形式が不正です (${brandTag})`,
        );
        return;
      }

      log(`✅ ブランドボタン${index + 1}: "${brandTag}"`);

      button.dataset.vfaInitialized = "true";
      checkBrandFavoriteStatus(brandTag, button);

      button.addEventListener(
        "click",
        function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggleBrandFavorite(button);
        },
        true,
      );
    });
  }

  /**
   * 初期化実行
   */
  function init() {
    console.log("[VFA Brand Favorite] 初期化開始");

    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        initializeBrandFavoriteButtons,
      );
    } else {
      setTimeout(initializeBrandFavoriteButtons, 100);
    }

    // 動的コンテンツ対応（MutationObserver）
    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(initializeBrandFavoriteButtons, 500);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // 初期化開始
  init();

  // デバッグ用グローバル関数
  window.vfaBrandFavoriteDebug = {
    reinit: initializeBrandFavoriteButtons,
    getButtons: () =>
      document.querySelectorAll(
        '.vfa-p-brand-header__favorite[data-vfa-initialized="true"]',
      ),
    checkStatus: (brandTag) => {
      const button = document.querySelector(`[data-brand-tag="${brandTag}"]`);
      if (button) checkBrandFavoriteStatus(brandTag, button);
    },
    listBrands: () => {
      const buttons = document.querySelectorAll(
        ".vfa-p-brand-header__favorite",
      );
      return Array.from(buttons).map((b) => ({
        tag: b.dataset.brandTag,
        id: b.dataset.brandId,
        favorited: b.classList.contains("vfa-registered"),
      }));
    },
  };
  log("📦 デバッグ関数が利用可能: window.vfaBrandFavoriteDebug");
})();
